import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import {
  createCasaMapaPartnerFreeListing,
  fetchCasaMapaPartnerAgency,
  fetchCasaMapaPartnerAgencyListings,
  fetchCasaMapaPartnerFreeListings,
  publishCasaMapaPartnerFreeListing,
  unpublishCasaMapaPartnerFreeListing,
} from "../../services/casamapaPartnerAgencyApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { useLocale, useCurrency } from "../../intl";
import "../../styles/partnerDealers.css";
import "../../styles/casamapaAdminPortal.css";

const FREE_COPY = "realEstate.casaMapa.admin.agencyPortal.freeListings";

const EMPTY_FORM = {
  title: "",
  listingType: "sale",
  priceCop: "",
  propertyType: "apartment",
  bedrooms: "",
  bathrooms: "",
  areaM2: "",
  neighborhood: "",
  municipality: "Medellín",
  addressText: "",
  photoUrl: "",
  description: "",
};

function formatCop(value, formatMoney) {
  if (value == null) return "—";
  return formatMoney(value);
}

function listingLabel(listing) {
  const parts = [listing.neighborhood, listing.municipality].filter(Boolean);
  return parts.length ? parts.join(", ") : listing.title;
}

export default function PartnerAgencyInventoryPage() {
  const { agencySlug } = useParams();
  const outletContext = useOutletContext() || {};
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const isAgencyPortal = Boolean(outletContext.agencyPortal);
  const [agency, setAgency] = useState(null);
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [freeListings, setFreeListings] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [freeError, setFreeError] = useState("");
  const [search, setSearch] = useState("");
  const [listingType, setListingType] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setFreeError("");
      const [agencyResult, listingsResult, freeResult] = await Promise.all([
        fetchCasaMapaPartnerAgency(agencySlug),
        fetchCasaMapaPartnerAgencyListings(agencySlug, {
          q: search.trim() || undefined,
          listingType: listingType || undefined,
          limit: 100,
        }),
        fetchCasaMapaPartnerFreeListings(agencySlug).catch((err) => {
          setFreeError(err.message || t(`${FREE_COPY}.loadError`));
          return null;
        }),
      ]);
      setAgency(agencyResult.agency || null);
      setListings(listingsResult.listings || []);
      setTotal(listingsResult.pagination?.total || listingsResult.listings?.length || 0);
      setFreeListings(freeResult?.listings || []);
      setUsage(freeResult?.usage || null);
    } catch (err) {
      setError(err.message || "Unable to load agency inventory.");
      setAgency(null);
      setListings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [agencySlug, listingType, search, t]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  async function handleCreateDraft(event) {
    event.preventDefault();
    try {
      setSaving(true);
      setFreeError("");
      await createCasaMapaPartnerFreeListing(agencySlug, {
        ...form,
        priceCop: form.priceCop === "" ? null : Number(form.priceCop),
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        areaM2: form.areaM2 === "" ? null : Number(form.areaM2),
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadInventory();
    } catch (err) {
      setFreeError(err.message || t(`${FREE_COPY}.saveError`));
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(listingId) {
    try {
      setBusyId(listingId);
      setFreeError("");
      await publishCasaMapaPartnerFreeListing(agencySlug, listingId);
      await loadInventory();
    } catch (err) {
      setFreeError(err.message || t(`${FREE_COPY}.publishError`));
    } finally {
      setBusyId("");
    }
  }

  async function handleUnpublish(listingId) {
    try {
      setBusyId(listingId);
      setFreeError("");
      await unpublishCasaMapaPartnerFreeListing(agencySlug, listingId);
      await loadInventory();
    } catch (err) {
      setFreeError(err.message || t(`${FREE_COPY}.publishError`));
    } finally {
      setBusyId("");
    }
  }

  const listingLimit = usage?.listingLimit ?? 5;

  return (
    <section className={`partner-dealers-page${isAgencyPortal ? " casamapa-admin-page" : ""}`}>
      <header className="partner-dealers-page__header casamapa-admin-page__header">
        <div>
          {!isAgencyPortal ? (
            <p className="partner-dashboard-hero__eyebrow">
              <Link to={path("agencies")}>{t("realEstate.casaMapa.admin.nav.agencies")}</Link> / Inventory
            </p>
          ) : null}
          <h2>{agency?.name || agencySlug}</h2>
          <p className="muted">
            {isAgencyPortal
              ? t("realEstate.casaMapa.admin.agencyPortal.inventorySubtitle")
              : t("realEstate.casaMapa.admin.agencyPortal.inventoryAdminSubtitle")}
          </p>
        </div>
        <div className="partner-dealers-page__stats">
          <span>{total.toLocaleString()} listings</span>
          <span>{agency?.saleListingCount ?? 0} for sale</span>
          <span>{agency?.rentListingCount ?? 0} for rent</span>
          {usage ? (
            <span>
              {t(`${FREE_COPY}.slots`, {
                used: usage.activePublished ?? 0,
                limit: listingLimit,
              })}
            </span>
          ) : null}
        </div>
      </header>

      <section className="partner-panel casamapa-free-listings" aria-labelledby="free-listings-title">
        <div className="partner-panel__header">
          <div>
            <h3 id="free-listings-title">{t(`${FREE_COPY}.title`)}</h3>
            <p className="muted">
              {t(`${FREE_COPY}.subtitle`, { limit: listingLimit })}
              {usage?.atListingLimit
                ? ` ${t(`${FREE_COPY}.atLimit`)}`
                : usage
                  ? ` ${t(`${FREE_COPY}.remaining`, { count: usage.listingsRemaining ?? 0 })}`
                  : ""}
            </p>
          </div>
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            onClick={() => setShowForm((open) => !open)}
          >
            {t(`${FREE_COPY}.addListing`)}
          </button>
        </div>

        {freeError ? <p className="admin-data-health-error">{freeError}</p> : null}

        {showForm ? (
          <form className="casamapa-free-listings__form" onSubmit={handleCreateDraft}>
            <h4>{t(`${FREE_COPY}.formTitle`)}</h4>
            <div className="casamapa-free-listings__grid">
              <label>
                <span>{t(`${FREE_COPY}.fields.title`)}</span>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                />
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.listingType`)}</span>
                <select
                  value={form.listingType}
                  onChange={(e) => setForm((prev) => ({ ...prev, listingType: e.target.value }))}
                >
                  <option value="sale">{t(`${FREE_COPY}.fields.sale`)}</option>
                  <option value="rent">{t(`${FREE_COPY}.fields.rent`)}</option>
                </select>
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.price`)}</span>
                <input
                  type="number"
                  min="0"
                  value={form.priceCop}
                  onChange={(e) => setForm((prev) => ({ ...prev, priceCop: e.target.value }))}
                />
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.propertyType`)}</span>
                <input
                  value={form.propertyType}
                  onChange={(e) => setForm((prev) => ({ ...prev, propertyType: e.target.value }))}
                />
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.bedrooms`)}</span>
                <input
                  type="number"
                  min="0"
                  value={form.bedrooms}
                  onChange={(e) => setForm((prev) => ({ ...prev, bedrooms: e.target.value }))}
                />
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.bathrooms`)}</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.bathrooms}
                  onChange={(e) => setForm((prev) => ({ ...prev, bathrooms: e.target.value }))}
                />
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.area`)}</span>
                <input
                  type="number"
                  min="0"
                  value={form.areaM2}
                  onChange={(e) => setForm((prev) => ({ ...prev, areaM2: e.target.value }))}
                />
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.neighborhood`)}</span>
                <input
                  value={form.neighborhood}
                  onChange={(e) => setForm((prev) => ({ ...prev, neighborhood: e.target.value }))}
                />
              </label>
              <label>
                <span>{t(`${FREE_COPY}.fields.municipality`)}</span>
                <input
                  value={form.municipality}
                  onChange={(e) => setForm((prev) => ({ ...prev, municipality: e.target.value }))}
                />
              </label>
              <label className="casamapa-free-listings__span2">
                <span>{t(`${FREE_COPY}.fields.address`)}</span>
                <input
                  value={form.addressText}
                  onChange={(e) => setForm((prev) => ({ ...prev, addressText: e.target.value }))}
                />
              </label>
              <label className="casamapa-free-listings__span2">
                <span>{t(`${FREE_COPY}.fields.photoUrl`)}</span>
                <input
                  value={form.photoUrl}
                  onChange={(e) => setForm((prev) => ({ ...prev, photoUrl: e.target.value }))}
                />
              </label>
              <label className="casamapa-free-listings__span2">
                <span>{t(`${FREE_COPY}.fields.description`)}</span>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </label>
            </div>
            <div className="casamapa-free-listings__actions">
              <button type="submit" className="partner-dashboard-btn partner-dashboard-btn--primary" disabled={saving}>
                {t(`${FREE_COPY}.saveDraft`)}
              </button>
              <button
                type="button"
                className="partner-dashboard-btn"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                {t(`${FREE_COPY}.cancel`)}
              </button>
            </div>
          </form>
        ) : null}

        {freeListings.length ? (
          <ul className="casamapa-free-listings__list">
            {freeListings.map((listing) => (
              <li key={listing.id}>
                <div>
                  <strong>{listing.title}</strong>
                  <p className="muted">
                    {listing.listingType} · {formatCop(listing.priceCop, formatMoney)} ·{" "}
                    {listing.status === "published" ? t(`${FREE_COPY}.published`) : t(`${FREE_COPY}.draft`)}
                  </p>
                </div>
                <div className="casamapa-free-listings__row-actions">
                  {listing.status === "published" ? (
                    <button
                      type="button"
                      className="partner-dashboard-btn"
                      disabled={busyId === listing.id}
                      onClick={() => handleUnpublish(listing.id)}
                    >
                      {t(`${FREE_COPY}.unpublish`)}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="partner-dashboard-btn partner-dashboard-btn--primary"
                      disabled={busyId === listing.id || usage?.atListingLimit}
                      onClick={() => handlePublish(listing.id)}
                    >
                      {t(`${FREE_COPY}.publish`)}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t(`${FREE_COPY}.empty`)}</p>
        )}
      </section>

      <div className="admin-users-toolbar">
        <input
          type="search"
          placeholder="Search address, neighborhood, or type"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={listingType} onChange={(event) => setListingType(event.target.value)}>
          <option value="">All types</option>
          <option value="sale">For sale</option>
          <option value="rent">For rent</option>
        </select>
        <button type="button" className="midpoint-share-button" onClick={loadInventory}>
          Refresh
        </button>
        <Link className="partner-dashboard-btn" to={path("listing-corrections")}>
          {t("realEstate.casaMapa.admin.nav.corrections")}
        </Link>
        {!isAgencyPortal ? (
          <Link className="partner-dashboard-btn" to={path("agencies")}>
            {t("realEstate.casaMapa.admin.partnerDashboard.yourAgencies")}
          </Link>
        ) : null}
      </div>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">Loading inventory…</p> : null}

      {!loading && listings.length ? (
        <table className="admin-data-health-table account-security-table">
          <thead>
            <tr>
              <th>Listing</th>
              <th>Type</th>
              <th>Property</th>
              <th>Price</th>
              <th>Beds</th>
              <th>Area</th>
              <th>Source</th>
              <th>Public</th>
            </tr>
          </thead>
          <tbody>
            {listings.map((listing) => (
              <tr key={listing.id}>
                <td>
                  <div className="partner-inventory-listing-cell">
                    {listing.photoUrl ? (
                      <img src={listing.photoUrl} alt="" className="partner-inventory-listing-cell__photo" />
                    ) : (
                      <span className="partner-inventory-listing-cell__placeholder" aria-hidden="true">
                        ⌂
                      </span>
                    )}
                    <div>
                      <strong>{listing.title}</strong>
                      <div className="muted">{listingLabel(listing)}</div>
                    </div>
                  </div>
                </td>
                <td>{listing.listingType || "—"}</td>
                <td>{listing.propertyType || "—"}</td>
                <td>{formatCop(listing.priceCop, formatMoney)}</td>
                <td>{listing.bedrooms ?? "—"}</td>
                <td>{listing.areaSqm ? `${listing.areaSqm} m²` : "—"}</td>
                <td>{listing.sourceKey || "—"}</td>
                <td>
                  {listing.publicUrl ? (
                    <a href={listing.publicUrl} target="_blank" rel="noreferrer">
                      View
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {!loading && !listings.length ? (
        <p className="muted">No listings matched your filters for this agency.</p>
      ) : null}
    </section>
  );
}
